import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { paths } from '@fleet/environment';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { InjectionToken } from '@angular/core';
import { merge } from 'lodash-es';

import { DOCUMENT } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { OnScreenNotification, OnscreenNotificationService } from '@fleet/ui';
import { failureNotification } from '@fleet/utilities';
import { generateStylesByColorHex } from './brand-utilities/css-theme-generator';
import * as WebFont from 'webfontloader';

import { LocaleService } from '@fleet/locale';
import { NetworkGroupApiService } from '@fleet/api';
import { BrandSettingModel, NetworkGroupModel } from '@fleet/model';

import { fleetBrand } from './brand_assets/fleet-brand';

import { TranslocoService } from '@jsverse/transloco';
export const PRODUCT_CONFIG = new InjectionToken<any>('PRODUCT_CONFIG');

@Injectable({
  providedIn: 'root',
})
export class ProductConfigurationService {
  productConfiguration: BehaviorSubject<any> = new BehaviorSubject(null);
  nonAuthorisedHttp: HttpClient;
  private _config: BehaviorSubject<any>;
  host: string;
  headers: any;
  fleetProduct: string;
  originalBrand: BrandSettingModel;
  brand: BrandSettingModel;
  networkConfig: BehaviorSubject<any> = new BehaviorSubject(null);
  constructor(
    private http: HttpClient,
    private backend: HttpBackend,
    @Inject('env') env: any,
    @Inject(PRODUCT_CONFIG) config: any,
    private onScreenNotificationService: OnscreenNotificationService,
    @Inject(DOCUMENT) private readonly document: Document,
    private titleService: Title,
    private localeService: LocaleService,
    private networkGroupApi: NetworkGroupApiService,
    private translocoService: TranslocoService
  ) {
    this.nonAuthorisedHttp = new HttpClient(backend);
    this.host = env.host + paths.productConfiguration;
    this.fleetProduct = env.fleetProduct;
    this._config = new BehaviorSubject(config);
    this.headers = new HttpHeaders({
      'fleet-channel': 'WEB',
      'fleet-product': env.fleetProduct,
    });
  }

  get networkConfig$() {
    return this.networkConfig.asObservable();
  }

  get networkConfigValue() {
    return this.networkConfig.value;
  }

  getProductConfiguration(): Promise<any> {
    return this.nonAuthorisedHttp
      .get(this.host, { headers: this.headers })
      .pipe(
        tap((resp: any) => {
          this.networkConfig.next({
            networkId: resp.data.networkId,
            networkGroupId: resp.data.networkGroupId,
          });
          const hasLocalConfig = localStorage.getItem('brandConfig');
          if (hasLocalConfig) {
            this.originalBrand = JSON.parse(hasLocalConfig);
            this.loadBrandConfig(this.originalBrand);
          } else {
            const brand = resp.data;
            // brand['locale'] = 'es';

            //HACK UNTIL SERVER IS SETUP
            // if (brand.displayName === 'Swan Taxis') {
            //   // brand = swanBrand;
            //   this.originalConfig = swanBrand;
            // } else if (brand.displayName === 'Flywheel') {
            //   this.originalConfig = flyWheelBrand;
            // } else if (brand.displayName === 'FLEET Demo') {
            //   this.originalConfig = fleetBrand;
            // } else {
            //   //ingogo for now
            //   this.originalConfig = fleetBrand;
            // }
            //HACK FOR GENERIC
            // brand = genericBrand;

            this.loadBrandConfig(brand);
          }
        }),
        catchError((error) => {
          console.log('Product config error:' + error);
          this.onScreenNotificationService.setNotification({
            ...failureNotification,
            title: 'Brand Initialisation Failed!',
            subTitle: 'Default settings applied',
          } as OnScreenNotification);
          this.originalBrand = fleetBrand;
          this.loadBrandConfig(this.originalBrand);
          return of(true);
        })
      )
      .toPromise();
  }

  loadBrandConfig(config: BrandSettingModel) {
    const favIcon: HTMLLinkElement = document.querySelector('#appIcon');
    const pageTitle = config.productNames.find(
      (s: any) => s.fleetProduct === this.fleetProduct
    ).name;

    this.titleService.setTitle(pageTitle);
    favIcon.href = config.favIcon;

    //Turn this off
    // config.locale = 'es';

    if (config.locale && config.locale !== this.localeService.getLocale()) {
      this.localeService.setLocale(config.locale);
      // this.translocoService.setActiveLang(
      //   config.locale.replace(/-/g, '_').toLowerCase()
      // );
      // this.localeService.setLocale('en-US');
    }
    if (config.currencyCode) {
      this.localeService.setCurrency(config.currencyCode);
      // this.localeService.setCurrency('USD');
    }

    this.setCssVariables(config.theme, this.document);
    if (config.font) {
      this.loadFont(config.font);
    }

    this.translocoService
      .load(`${config.locale.replace(/-/g, '_').toLowerCase()}`)
      .subscribe({});

    console.log(this.translocoService.getActiveLang());
    this.config = config;
    this.brand = config;
  }

  /**
   * Constructor
   */

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setter & getter for config
   */
  set config(value: any) {
    // Merge the new config over to the current config
    const config = merge({}, this._config.getValue(), value);

    // Execute the observable
    this._config.next(config);
  }

  get config$(): Observable<any> {
    return this._config.asObservable();
  }

  get configValue() {
    return this._config.value;
  }

  getLayout() {
    return this._config.value.layout as string;
  }

  publish() {
    localStorage.setItem('brandConfig', JSON.stringify(this._config.value));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Resets the config to the default
   */
  reset(): void {
    // Set the config
    this._config.next(this.config);
  }

  setCssVariables(theme: any, document: Document) {
    let styles: any[] = [];
    styles = [...styles, ...generateStylesByColorHex(theme.primary, 'primary')];

    if (theme.accent) {
      styles = [...styles, ...generateStylesByColorHex(theme.accent, 'accent')];
    }

    //warn
    styles = [
      ...styles,
      ...generateStylesByColorHex(theme.warn ? theme.warn : '#dc2626', 'warn'),
    ];

    //control colors
    styles = [
      ...styles,
      ...generateStylesByColorHex(
        theme.controls ? theme.controls : theme.primary,
        'controls'
      ),
    ];

    styles.forEach((style: any) => {
      document.body.style.setProperty(style.var, style.value);
    });
  }

  loadFont(font: string) {
    WebFont.load({
      google: {
        families: [font],
      },
    });
    document.documentElement.style.setProperty('--app-font', font);
  }
}
